/** Iconos del calendario. glyph queda como fallback textual. */
export const SIMBOLOS = [
  { id: "meeting", glyph: "●", color: "#db2777", picker: true },
  { id: "shopping", glyph: "▣", color: "#ea580c", picker: true },
  { id: "viaje", glyph: "▲", color: "#0284c7", picker: true },
  { id: "china", glyph: "🏯", color: "#dc2626", picker: true },
  { id: "feria", glyph: "◆", color: "#d97706", picker: true },
  { id: "nota", glyph: "·", color: "#64748b", picker: false },
  { id: "festival", glyph: "✦", color: "#7c3aed", picker: false },
  { id: "inicio", glyph: "✱", color: "#0f766e", picker: false },
];

export const SIMBOLOS_PICKER = SIMBOLOS.filter((item) => item.picker);

export const SEMESTERS = [
  [1, 2, 3, 4, 5, 6],
  [7, 8, 9, 10, 11, 12],
];

/** Color de temporada por mes: frío → primavera → verano → otoño. */
export const MONTH_ACCENTS = [
  "#5b7c9d", // ene
  "#6a8fad", // feb
  "#3d9a8b", // mar
  "#2f9e78", // abr
  "#c4a35a", // may
  "#d97706", // jun
  "#c45c26", // jul
  "#b45309", // ago
  "#a16207", // sep
  "#9a3412", // oct
  "#7c3a6e", // nov
  "#475569", // dic
];

export function monthAccent(month) {
  return (
    MONTH_ACCENTS[Math.max(0, Math.min(11, Number(month) - 1))] || "#0f766e"
  );
}

const WEEKDAY_LETTERS = {
  es: ["L", "M", "X", "J", "V", "S", "D"],
  en: ["M", "T", "W", "T", "F", "S", "S"],
};

export function simboloById(id) {
  return (
    SIMBOLOS.find((item) => item.id === id) ||
    SIMBOLOS.find((s) => s.id === "nota")
  );
}

/** Corrige iconos viejos (viaje/nota) según el texto de la entrada. */
export function inferSimboloFromTexto(texto, fallback = "nota") {
  const t = String(texto || "").toUpperCase();
  if (/\bSHOPPING\b/.test(t)) return "shopping";
  if (/\bCHINA\b|\bSUZHOU\b/.test(t)) return "china";
  if (/\bFERIA\b|\bTEXWORLD\b|\bPREMI[EÈ]RE\b|\bPV\b/.test(t)) return "feria";
  if (/\bVIAJE\b|\bFLIGHT\b|\bLONDON\b|\bPARIS\b|\bMILAN\b/.test(t)) {
    return "viaje";
  }
  if (/\bMEETING\b|\bREUNI[OÓ]N\b/.test(t)) return "meeting";
  if (/\bFESTIV|\bHOLIDAY\b|\bPUENTE\b/.test(t)) return "festival";
  return fallback;
}

export function notaSimbolos(nota) {
  let ids = [];
  if (Array.isArray(nota?.Simbolos) && nota.Simbolos.length) {
    ids = nota.Simbolos.map(String).filter(Boolean);
  } else if (typeof nota?.Simbolos === "string" && nota.Simbolos.trim()) {
    try {
      const parsed = JSON.parse(nota.Simbolos);
      if (Array.isArray(parsed) && parsed.length) {
        ids = parsed.map(String).filter(Boolean);
      }
    } catch {
      /* ignore */
    }
  }
  if (!ids.length) {
    const one = String(nota?.Simbolo || "nota").trim() || "nota";
    ids = [one];
  }
  // Si quedó un icono genérico/viejo, deducir del texto.
  const inferred = inferSimboloFromTexto(nota?.Texto, ids[0]);
  const primary = ids[0];
  const shouldReplace =
    inferred !== primary &&
    (primary === "nota" ||
      primary === "festival" ||
      (primary === "viaje" && inferred === "shopping") ||
      (primary === "viaje" &&
        inferred === "china" &&
        !/\bVIAJE\b/i.test(String(nota?.Texto || ""))));
  if (shouldReplace) {
    if (ids.length === 1) return [inferred];
    return [
      inferred,
      ...ids.filter((id) => id !== inferred && id !== primary),
    ].slice(0, 5);
  }
  return ids;
}

export function notaFechaFin(nota) {
  const start = String(nota?.Fecha || "").slice(0, 10);
  const fin = String(nota?.FechaFin || "").slice(0, 10);
  if (!start) return "";
  if (!fin || fin < start) return start;
  return fin;
}

export function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

export function toIsoDate(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function todayIso(now = new Date()) {
  return toIsoDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

export function addDaysIso(iso, days) {
  const match = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]) + Number(days || 0),
  );
  return toIsoDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/** 0 = lunes … 6 = domingo. */
export function mondayIndex(iso) {
  const match = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return 0;
  const jsDay = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  ).getDay();
  return (jsDay + 6) % 7;
}

export function weekdayLetter(iso, lang) {
  const letters = String(lang || "")
    .toLowerCase()
    .startsWith("es")
    ? WEEKDAY_LETTERS.es
    : WEEKDAY_LETTERS.en;
  return letters[mondayIndex(iso)];
}

export function isWeekend(iso) {
  return mondayIndex(iso) >= 5;
}

/** Semana de lunes a domingo. */
export function weekBounds(iso) {
  const match = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const start = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]) - mondayIndex(iso),
  );
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return {
    start: toIsoDate(
      start.getFullYear(),
      start.getMonth() + 1,
      start.getDate(),
    ),
    end: toIsoDate(end.getFullYear(), end.getMonth() + 1, end.getDate()),
  };
}

export function monthName(year, month, lang) {
  const code = String(lang || "es").toLowerCase();
  const locale = code.startsWith("es")
    ? "es"
    : code.startsWith("zh") || code === "ch"
      ? "zh-CN"
      : code.startsWith("da") || code === "dk"
        ? "da"
        : code.startsWith("nl")
          ? "nl"
          : "en";
  const label = new Intl.DateTimeFormat(locale, { month: "long" }).format(
    new Date(year, month - 1, 1),
  );
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Expande notas multi-día a cada fecha del rango. */
export function groupNotasByFecha(notas) {
  const map = new Map();
  for (const nota of notas || []) {
    const start = String(nota.Fecha || "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) continue;
    const end = notaFechaFin(nota) || start;
    let cursor = start;
    let guard = 0;
    while (cursor && cursor <= end && guard < 400) {
      if (!map.has(cursor)) map.set(cursor, []);
      map.get(cursor).push(nota);
      cursor = addDaysIso(cursor, 1);
      guard += 1;
    }
  }
  return map;
}

export function groupAccionesByMonth(acciones) {
  const map = new Map();
  for (const accion of acciones || []) {
    const month = Number(accion.Mes);
    if (!map.has(month)) map.set(month, []);
    map.get(month).push(accion);
  }
  for (const list of map.values()) {
    list.sort(
      (a, b) =>
        String(a.FechaAviso).localeCompare(String(b.FechaAviso)) ||
        Number(a.Id) - Number(b.Id),
    );
  }
  return map;
}

export function formatDiaMes(iso) {
  const match = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "";
  return `${match[3]}/${match[2]}`;
}

export function notaSpanLabel(nota) {
  const start = String(nota?.Fecha || "").slice(0, 10);
  const end = notaFechaFin(nota);
  if (!start || !end || start === end) return "";
  return `${formatDiaMes(start)}–${formatDiaMes(end)}`;
}

/**
 * Recorta una nota al mes dado. Si no solapa, null.
 * @returns {{ startDay: number, endDay: number, continuesBefore: boolean, continuesAfter: boolean } | null}
 */
export function clipNotaToMonth(nota, year, month) {
  const y = Number(year);
  const m = Number(month);
  if (!Number.isInteger(y) || !Number.isInteger(m) || m < 1 || m > 12) {
    return null;
  }
  const startIso = String(nota?.Fecha || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startIso)) return null;
  const endIso = notaFechaFin(nota) || startIso;
  const monthStart = toIsoDate(y, m, 1);
  const length = daysInMonth(y, m);
  const monthEnd = toIsoDate(y, m, length);
  if (endIso < monthStart || startIso > monthEnd) return null;

  const clipStart = startIso < monthStart ? monthStart : startIso;
  const clipEnd = endIso > monthEnd ? monthEnd : endIso;
  const startDay = Number(clipStart.slice(8, 10));
  const endDay = Number(clipEnd.slice(8, 10));
  if (
    !Number.isInteger(startDay) ||
    !Number.isInteger(endDay) ||
    startDay < 1 ||
    endDay < startDay
  ) {
    return null;
  }
  return {
    startDay,
    endDay,
    continuesBefore: startIso < monthStart,
    continuesAfter: endIso > monthEnd,
  };
}

/**
 * Asigna carriles greedy a segmentos { startDay, endDay }.
 * Mutates each segment with `lane` y `laneCount` (por cluster de solape,
 * no el máximo del mes entero — así no se apilan a ancho completo).
 */
export function assignNotaLanes(segments) {
  const list = Array.isArray(segments) ? segments : [];
  const sorted = [...list].sort(
    (a, b) =>
      Number(a.startDay) - Number(b.startDay) ||
      Number(b.endDay) -
        Number(b.startDay) -
        (Number(a.endDay) - Number(a.startDay)) ||
      Number(a.nota?.Id || 0) - Number(b.nota?.Id || 0),
  );
  /** Último día ocupado por carril (fin inclusive). */
  const laneEnds = [];
  for (const seg of sorted) {
    let lane = 0;
    for (; lane < laneEnds.length; lane += 1) {
      if (laneEnds[lane] < seg.startDay) break;
    }
    if (lane === laneEnds.length) laneEnds.push(seg.endDay);
    else laneEnds[lane] = seg.endDay;
    seg.lane = lane;
  }

  // Concurrencia máxima en los días que toca cada segmento → ancho del bloque.
  const covering = (day) =>
    list.filter((s) => Number(s.startDay) <= day && day <= Number(s.endDay));

  for (const seg of list) {
    let maxConcurrent = 1;
    for (let day = seg.startDay; day <= seg.endDay; day += 1) {
      const onDay = covering(day);
      const localMax =
        Math.max(0, ...onDay.map((s) => Number(s.lane) || 0)) + 1;
      if (localMax > maxConcurrent) maxConcurrent = localMax;
    }
    seg.laneCount = Math.max(1, maxConcurrent);
  }

  return Math.max(1, ...list.map((s) => (Number(s.lane) || 0) + 1), 1);
}

/**
 * Layout de barras para un mes: un segmento por nota que solapa el mes.
 * @returns {Array<{ nota, startDay, endDay, lane, laneCount, continuesBefore, continuesAfter }>}
 */
export function layoutMonthNotas(notas, year, month) {
  const segments = [];
  for (const nota of notas || []) {
    const clip = clipNotaToMonth(nota, year, month);
    if (!clip) continue;
    segments.push({
      nota,
      startDay: clip.startDay,
      endDay: clip.endDay,
      continuesBefore: clip.continuesBefore,
      continuesAfter: clip.continuesAfter,
      lane: 0,
      laneCount: 1,
    });
  }
  assignNotaLanes(segments);
  segments.sort(
    (a, b) =>
      a.startDay - b.startDay ||
      a.lane - b.lane ||
      Number(a.nota?.Id || 0) - Number(b.nota?.Id || 0),
  );
  return segments;
}

/** Notas que tocan un día concreto (para color / popover), sin expandir UI. */
export function notasTouchingFecha(notas, fecha) {
  const day = String(fecha || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return [];
  return (notas || []).filter((nota) => {
    const start = String(nota?.Fecha || "").slice(0, 10);
    if (!start) return false;
    const end = notaFechaFin(nota) || start;
    return start <= day && day <= end;
  });
}
